import {
  ApolloClient,
  ApolloLink,
  defaultDataIdFromObject,
  type DefaultOptions,
  from,
  HttpLink,
  InMemoryCache,
  split,
} from "@apollo/client";
import { BatchHttpLink } from "@apollo/client/link/batch-http";

import {
  getBackendUrlForEnv,
  readPersistedEnv,
} from "./components/crown-data-import/env/env-config";

const cache = new InMemoryCache({
  dataIdFromObject: (responseObject) => {
    return (
      (responseObject.uuid as string) ?? defaultDataIdFromObject(responseObject)
    );
  },
});

const operationsToNotBatch = new Set([
  "orderVolumeByDateRangeReport",
  "orderVolumeByContactReport",
  "allScannedDocuments",
  "schemas",
]);

/**
 * Per-operation link that resolves the target GraphQL endpoint from the
 * currently selected Crown env. Reading the persisted env at request time
 * lets env switches take effect without rebuilding the client.
 */
const envLink = new ApolloLink((operation, forward) => {
  const env = readPersistedEnv();
  operation.setContext({ uri: `${getBackendUrlForEnv(env)}/graphql` });
  return forward(operation);
});

/**
 * App-wide Apollo defaults. Exported so a spec runs a page under the same
 * no-cache policy it meets in the app.
 */
export const apolloDefaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: "no-cache",
    errorPolicy: "ignore",
  },
  query: {
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  },
  mutate: {
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  },
};

const apolloClient = new ApolloClient({
  cache,
  credentials: "include",
  uri: `${getBackendUrlForEnv(readPersistedEnv())}/graphql`,
  link: from([
    envLink,
    split(
      (operation) => operationsToNotBatch.has(operation.operationName),
      new HttpLink({ credentials: "include" }),
      new BatchHttpLink({ credentials: "include" }),
    ),
  ]),
  // disable apollo cache
  defaultOptions: apolloDefaultOptions,
});

export default apolloClient;
